/**
 * Settings Tab Component
 *
 * Counter priority order, the per-scope sections and (for admins) the admin config.
 * Counters are marked unavailable on the counter grid itself (press and hold → Unavailable).
 */

import { CounterPriorityOrderSection } from "@/components/CounterPriorityOrderSection";
import { AdminConfigPanel } from "@/components/admin/AdminConfigPanel";
import { SettingsScopeSections } from "@/components/SettingsScopeSections";
import type { DutyUse } from "@/utils/dutyOptions";

interface SettingsTabProps {
  generated: boolean;
  operation_mode?: string;
  shift_type?: string;
  onUpdated?: () => void;
  isAdmin?: boolean;
  /** Who is on each non-counter deployment (see utils/dutyOptions: dutyUsage) — for the remove confirmation. */
  dutyUse?: Record<string, DutyUse>;
}

export function SettingsTab({
  generated,
  operation_mode,
  shift_type,
  onUpdated,
  isAdmin,
  dutyUse,
}: SettingsTabProps) {
  if (!generated) {
    return (
      <div>
        <div
          className="ui-zoom"
          style={{
            background: "white",
            borderRadius: "8px",
            padding: "30px",
            textAlign: "center",
            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          }}
        >
          <h2>Generate a schedule first</h2>
          <p style={{ color: "#666" }}>
            You need to generate a roster before accessing settings
          </p>
        </div>
        <SettingsScopeSections dutyUse={dutyUse} />
        {isAdmin && (
          <AdminConfigPanel
            operationMode={operation_mode}
            shiftType={shift_type}
            generated={generated}
            onApplied={onUpdated}
          />
        )}
      </div>
    );
  }

  return (
    <div
      className="ui-zoom"
      style={{
        background: "white",
        borderRadius: "8px",
        padding: "12px",
        boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
      }}
    >
      <h2 style={{ marginTop: 0, marginBottom: "8px" }}>⚙️ Settings</h2>

      {/* ── Priority counters ────────────────────────────────────────────── */}
      <div style={{ borderTop: "1px solid #e5e7eb", paddingTop: 16, marginBottom: 16 }}>
        <h4 style={{ margin: "0 0 8px", fontSize: "19px", fontWeight: 700, letterSpacing: "-0.01em", color: "#0f172a" }}>
          Priority Counters
        </h4>
        <CounterPriorityOrderSection operation_mode={operation_mode} shift_type={shift_type} />
      </div>

      <SettingsScopeSections dutyUse={dutyUse} />

      {isAdmin && (
        <AdminConfigPanel
          operationMode={operation_mode}
          shiftType={shift_type}
          generated={generated}
          onApplied={onUpdated}
        />
      )}
    </div>
  );
}
